import { useContext } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowDown, faArrowUp, faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import {
  Button, Form, ModalContext, SectionDivider, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useCampaignQuoteTemplateSection, {
  useCampaignQuoteTemplateSectionForm,
} from '@hooks/useCampaignQuoteTemplateSection'

import { confirmDelete } from '@functions/entity'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import ToggleListItem from '@components/ToggleListItem'

const MODAL_KEY = 'CreateOrEditCampaignQuoteTemplateSectionModal'

type MoveSectionParams = {
  callbacks: { closeModal: () => void },
  campaignQuoteSectionIds: number[],
  direction: string,
  fromIndex: number,
  moveFn: Function,
}

const moveSection = (params: MoveSectionParams) => {
  const {
    // @ts-expect-error TS2339
    callbacks, campaignQuoteTemplateSectionIds, direction, fromIndex, moveFn,
  } = params
  const { closeModal } = callbacks

  const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1

  moveFn({ campaignQuoteTemplateSectionIds, fromIndex, toIndex }).then(() => {
    // Need to close the modal so that when can reset the index when editing the section again
    closeModal()
  })
}

type ModalCallbacks = {
  closeModal: () => void,
  createCampaignQuoteTemplateSection: (payload: HandleCallbackActionParams) => void,
  updateCampaignQuoteTemplateSection: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditCampaignQuoteTemplateSectionModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, createCampaignQuoteTemplateSection, updateCampaignQuoteTemplateSection } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const {
    index, first, last, campaignQuoteTemplateSectionIds,
  } = modalPayload

  const campaignQuoteTemplateSection = digObject(modalPayload, 'campaignQuoteTemplateSection', {})

  const newRecord = !campaignQuoteTemplateSection.id

  const {
    callbacks: {
      createCampaignQuoteTemplateSection: createFn,
      deleteCampaignQuoteTemplateSection: deleteFn,
      moveQuoteTemplateSectionId: moveFn,
      updateCampaignQuoteTemplateSection: updateFn,
    },
    creating,
    updating,
  } = useCampaignQuoteTemplateSection(campaignQuoteTemplateSection)

  const {
    entityState,
    entityState: {
      footer,
      header,
      hide_dates,
      hide_prices,
      show_subtotal,
      title,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = useCampaignQuoteTemplateSectionForm(campaignQuoteTemplateSection, { validateOn: 'change' })

  const createOrUpdateQuoteTemplateSectionPayload = {
    callbacks: {
      action: newRecord ? createFn : updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: `Quote Template Section ${newRecord ? 'Created' : 'Updated'}`,
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header
        callbacks={callbacks}
        title={newRecord ? 'Create Quote' : 'Edit Quote'}
        titleSecondLine="Template Section"
      />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Title" errorMessage={errors.title}>
            <input name="title" type="text" value={title} {...handlers} />
          </Form.Field>

          <Form.Field label="Header" errorMessage={errors.header} marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <textarea name="header" value={header} style={{ height: 80 }} {...handlers} />
          </Form.Field>

          <Form.Field label="Footer" errorMessage={errors.footer} marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <textarea name="footer" value={footer} style={{ height: 80 }} {...handlers} />
          </Form.Field>

          <SectionDivider boxProps={{ marginY: 'large' }}>PDF Settings</SectionDivider>

          <ToggleListItem
            onClick={() => setEntityState({ hide_dates: !hide_dates })}
            toggleOffColor="bodyFontLightColor"
            toggleOffText="No"
            toggleOnColor="blue"
            toggleOnText="Yes"
            toggleValue={hide_dates}
          >
            <ToggleListItem.Title>Hide Dates</ToggleListItem.Title>
            <ToggleListItem.Description>
              When enabled, item dates will not be visible on the downloadable PDF.
            </ToggleListItem.Description>
          </ToggleListItem>

          <ToggleListItem
            onClick={() => setEntityState({ hide_prices: !hide_prices })}
            toggleOffColor="bodyFontLightColor"
            toggleOffText="No"
            toggleOnColor="blue"
            toggleOnText="Yes"
            toggleValue={hide_prices}
          >
            <ToggleListItem.Title>Hide Prices</ToggleListItem.Title>
            <ToggleListItem.Description>
              When enabled, item prices will not be visible on the downloadable PDF.
            </ToggleListItem.Description>
          </ToggleListItem>

          <ToggleListItem
            onClick={() => setEntityState({ show_subtotal: !show_subtotal })}
            toggleOffColor="bodyFontLightColor"
            toggleOffText="No"
            toggleOnColor="blue"
            toggleOnText="Yes"
            toggleValue={show_subtotal}
          >
            <ToggleListItem.Title>Show Subtotal</ToggleListItem.Title>
            <ToggleListItem.Description>
              When enabled, section subtotal will be shown on the downloadable PDF.
            </ToggleListItem.Description>
          </ToggleListItem>

          <Form.Field label="More Options" marginTop="large">
            <Button
              buttonStyle="secondaryUtility"
              icon={<FontAwesomeIcon icon={faTrashAlt} />}
              onClick={() => confirmDelete({
                callbacks: {
                  afterAction: closeModal,
                  deleteFn,
                },
                strings: {
                  entityName: 'Quote Template Section',
                },
              })}
              marginTop="medium"
              size="medium"
            >
              Delete Section
            </Button>
          </Form.Field>

          {!(first && last) && (
            <Form.Field label="Position" marginTop="large">
              {!first && (
                <Button
                  buttonStyle="secondaryUtility"
                  icon={<FontAwesomeIcon icon={faArrowUp} />}
                  onClick={() => moveSection({
                    callbacks,
                    // @ts-expect-error TS2561
                    campaignQuoteTemplateSectionIds,
                    direction: 'up',
                    fromIndex: index,
                    moveFn,
                  })}
                  size="medium"
                >
                  Move Section Up
                </Button>
              )}

              {!last && (
                <Button
                  buttonStyle="secondaryUtility"
                  icon={<FontAwesomeIcon icon={faArrowDown} />}
                  marginTop="medium"
                  onClick={() => moveSection({
                    callbacks,
                    // @ts-expect-error TS2561
                    campaignQuoteTemplateSectionIds,
                    direction: 'down',
                    fromIndex: index,
                    moveFn,
                  })}
                  size="medium"
                >
                  Move Section Down
                </Button>
              )}
            </Form.Field>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          onClick={
            newRecord
              ? () => createCampaignQuoteTemplateSection(createOrUpdateQuoteTemplateSectionPayload)
              : () => updateCampaignQuoteTemplateSection(createOrUpdateQuoteTemplateSectionPayload)
          }
          size="large"
        >
          {saveEnabled ? `${newRecord ? 'Create' : 'Update'} Quote Template Section` : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditCampaignQuoteTemplateSectionModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
